import type {
  DetailTemplateTypeCd,
  InteriorReviewTag,
  ConstructionCaseInfo,
} from '../model';

/**
 * 개편 화면 대상 여부.
 * 미정의 값·null·undefined 는 전부 기존 화면(STANDARD)으로 폴백한다. (TS-003)
 * 앱팀 합의 규칙과 동일 — 서버가 새 값을 먼저 내려도 구버전이 깨지지 않는다.
 */
export const isInteriorTemplate = (
  code?: DetailTemplateTypeCd | string | null,
): boolean => code === 'INTERIOR';

/** NewFlag 에 넘길 props (icon 제외) */
export interface InteriorFlagProps {
  variant: 'line' | 'solid';
  content: string;
  color: string;
  textColor: string;
  fontStyle: 'regular' | 'bold';
}

/**
 * 후기 태그 → NewFlag props. (TS-011)
 * color/textColor 는 서버 제어 스타일이므로 토큰으로 치환하지 않고 그대로 전달한다.
 */
export const toNewFlagProps = (
  tags?: InteriorReviewTag[] | null,
): InteriorFlagProps[] =>
  (tags ?? []).map((t) => ({
    variant: t.style === 'SOLID' ? 'solid' : 'line',
    content: t.name,
    color: t.styleColor,
    textColor: t.textColor,
    fontStyle: t.fontStyle === 'bold' ? 'bold' : 'regular',
  }));

/**
 * 메타 조각에서 빈 값·null 을 걸러 앞에서부터 채운다. (JRUK-37)
 *
 * 슬롯 기반 UI(`VerticalContentUnit` 의 info1/info2)는 슬롯마다 구분자를 붙이므로,
 * 앞 슬롯에 null 을 그대로 두면 구분자만 남을 수 있다. 마이그레이션 후기는 평형대가
 * null 로 내려오므로(JRUK-37) 꽂기 전에 이 함수로 압축한다.
 */
export const compactMeta = (parts: (string | null | undefined)[]): string[] =>
  parts.filter((p): p is string => !!p && p.trim() !== '');

/**
 * 메타 라인 조립. (TS-013)
 * 빈 값·null 을 걸러낸 뒤 구분자로 잇는다 — 빈 값이 있어도 구분자가 남지 않는다.
 */
export const joinMeta = (
  parts: (string | null | undefined)[],
  separator = ' | ',
): string => compactMeta(parts).join(separator);

/** 시공사례 팝업 행 */
export interface ConstructionCaseRow {
  label: string;
  value: string;
}

/**
 * 시공사례 상세 팝업의 행 목록. (TS-014)
 *
 * 어드민 미등록 케이스 정책 (07.28 개정 — 기획 확인으로 PC 도 MO 와 동일 적용):
 * - 미등록(null·빈 문자열) 필드는 행을 유지하고 값을 "-" 로 표기한다.
 * - 그룹의 필드가 모두 미등록이면 빈 배열을 반환한다 → 소비처가 영역 자체를 미노출.
 * 라벨 "예산"은 JRUK-23 07-28 확정분 (시안의 "분야"는 변경 전 버전).
 */
export const toConstructionCaseRows = (
  info?: ConstructionCaseInfo | null,
): { primary: ConstructionCaseRow[]; secondary: ConstructionCaseRow[] } => {
  const buildRows = (
    defs: [label: string, value: string | null | undefined][],
  ): ConstructionCaseRow[] => {
    const values = defs.map(([, v]) => (v && v.trim() !== '' ? v : null));
    if (values.every((v) => v === null)) return [];
    return defs.map(([label], i) => ({ label, value: values[i] ?? '-' }));
  };

  return {
    primary: buildRows([
      ['주거유형', info?.houseType],
      ['면적', info?.area],
      ['예산', info?.cost],
      ['가족유형', info?.familyType],
      ['스타일', info?.style],
    ]),
    secondary: buildRows([
      ['지역', info?.region],
      ['확장여부', info?.extensions],
      ['세부공사', info?.detailConstructions],
    ]),
  };
};
